import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';

const money = (v) => v == null ? '—' : `$${Number(v || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fixed = (v,d=2) => v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(d);
const setupLabel = (t) => t?.strategy_snapshot?.parameters?.setup_family || t?.strategy_name || 'Unknown';
const regimeLabel = (t) => t?.trade_context_snapshot?.regime?.label || t?.trade_context_snapshot?.chain_snapshot?.market?.label || '—';
const themeLabel = (t) => {
  const direct = (t?.trade_context_snapshot?.themes || []).map(x=>x.theme).filter(Boolean);
  if (direct.length) return direct.join(', ');
  const frozen = t?.trade_context_snapshot?.chain_snapshot?.theme;
  return frozen?.theme || frozen?.name || '—';
};

const Analytics = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [syntheticDemo, setSyntheticDemo] = useState(false);
  const [demoNotice, setDemoNotice] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, historyRes, intelligenceStatusRes] = await Promise.all([
          api.getPerformanceStats(), api.getTradeHistory(), api.getIntelligenceStatus()
        ]);
        const realTrades = historyRes.data || [];
        if (process.env.REACT_APP_LOCAL_EXECUTION === 'true' && process.env.REACT_APP_ALLOW_SYNTHETIC_UI === 'true' && !realTrades.length && intelligenceStatusRes.data?.synthetic_data) {
          const demoRes = await api.getDemoAnalytics();
          setStats(demoRes.data.stats);
          setTradeHistory(demoRes.data.trades || []);
          setSyntheticDemo(true);
          setDemoNotice(demoRes.data.notice || 'Synthetic research sample.');
        } else {
          setStats(statsRes.data);
          setTradeHistory(realTrades);
        }
      } catch (e) {
        console.error('Failed to fetch analytics', e);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="workspace-page"><LoadingSpinner/></div>;
  if (!stats) return <div className="workspace-page"><div className="ws-banner danger">Research data could not be loaded.</div></div>;

  const withR = tradeHistory.filter(t => t.r_multiple != null && Number.isFinite(Number(t.r_multiple)));
  const avgR = withR.length ? withR.reduce((s,t)=>s+Number(t.r_multiple),0)/withR.length : null;
  const withMfe = tradeHistory.filter(t => t.mfe_r != null && Number.isFinite(Number(t.mfe_r)));
  const avgMfe = withMfe.length ? withMfe.reduce((s,t)=>s+Number(t.mfe_r),0)/withMfe.length : null;
  const withMae = tradeHistory.filter(t => t.mae_r != null && Number.isFinite(Number(t.mae_r)));
  const avgMae = withMae.length ? withMae.reduce((s,t)=>s+Number(t.mae_r),0)/withMae.length : null;

  const strategyMap = {};
  tradeHistory.filter(t => t.pnl != null && Number.isFinite(Number(t.pnl))).forEach(t => {
    const key=setupLabel(t);
    strategyMap[key] ||= {strategy:key,trades:0,wins:0,totalR:0,rCount:0,pnl:0};
    const row=strategyMap[key]; row.trades++; row.pnl += Number(t.pnl||0);
    if(Number(t.pnl||0)>0) row.wins++;
    if(t.r_multiple != null && Number.isFinite(Number(t.r_multiple))){ row.totalR+=Number(t.r_multiple); row.rCount++; }
  });
  const strategies=Object.values(strategyMap).map(r=>({...r,winRate:r.trades?100*r.wins/r.trades:0,avgR:r.rCount?r.totalR/r.rCount:null})).sort((a,b)=>b.trades-a.trades);

  return <div className="workspace-page">
    <div className="page-header">
      <div className="page-header-copy">
        <div className="eyebrow">Post-trade research</div>
        <h1 className="page-title">Trade Research</h1>
        <p className="page-description">Measure expectancy, execution quality and strategy behavior in R — not just raw P/L.</p>
      </div>
    </div>

    {syntheticDemo && <div className="ws-banner warning"><div><strong>Synthetic research sample.</strong> {demoNotice}</div></div>}

    <div className="research-kpis">
      <div className="metric-card"><div className="metric-label">Net P/L</div><div className={`metric-value ${stats.total_net_pnl>=0?'positive':'negative'}`}>{money(stats.total_net_pnl)}</div><div className="metric-sub">{stats.total_trades} total trades</div></div>
      <div className="metric-card"><div className="metric-label">Average R</div><div className="metric-value">{fixed(avgR)}</div><div className="metric-sub">Per completed trade</div></div>
      <div className="metric-card"><div className="metric-label">Win rate</div><div className="metric-value">{fixed(stats.win_rate_percent,1)}%</div><div className="metric-sub">{stats.winning_trades}W · {stats.losing_trades}L</div></div>
      <div className="metric-card"><div className="metric-label">Profit factor</div><div className="metric-value">{fixed(stats.profit_factor)}</div><div className="metric-sub">Gross wins / gross losses</div></div>
      <div className="metric-card"><div className="metric-label">Max drawdown</div><div className="metric-value negative">{fixed(stats.max_drawdown_percent,1)}%</div><div className="metric-sub">Current research sample</div></div>
      <div className="metric-card"><div className="metric-label">Average MFE</div><div className="metric-value">{fixed(avgMfe)}R</div><div className="metric-sub">Best excursion</div></div>
      <div className="metric-card"><div className="metric-label">Average MAE</div><div className="metric-value">{fixed(avgMae)}R</div><div className="metric-sub">Worst excursion</div></div>
    </div>

    <div className="section-stack">
      <section className="ws-panel pad">
        <div className="section-header"><h2 className="section-title">Setup Breakdown</h2><span className="section-meta">Plan-linked research by setup family</span></div>
        {strategies.length ? <div className="ws-table-wrap"><table className="ws-table"><thead><tr><th>Setup</th><th className="number">Trades</th><th className="number">Win rate</th><th className="number">Avg R</th><th className="number">Net P/L</th></tr></thead><tbody>
          {strategies.map(r=><tr key={r.strategy}><td><strong>{r.strategy}</strong></td><td className="number">{r.trades}</td><td className="number">{fixed(r.winRate,1)}%</td><td className="number">{fixed(r.avgR)}</td><td className={`number ${r.pnl>=0?'positive':'negative'}`}>{money(r.pnl)}</td></tr>)}
        </tbody></table></div> : <div className="empty-state">No strategy research yet.</div>}
      </section>

      <section className="ws-panel pad">
        <div className="section-header"><h2 className="section-title">{syntheticDemo ? 'Synthetic Trade Sample' : 'Trade Journal'}</h2><span className="section-meta">{tradeHistory.length} records</span></div>
        {tradeHistory.length ? <div className="ws-table-wrap"><table className="ws-table"><thead><tr><th>Ticker</th><th>Setup</th><th>Plan</th><th className="number">P/L</th><th className="number">R</th><th className="number">MFE</th><th className="number">MAE</th><th>Regime</th><th>Theme</th><th className="number">Entry</th><th className="number">Exit</th><th>Closed</th></tr></thead><tbody>
          {tradeHistory.map(t=><tr key={t.id}><td><strong className="ticker">{t.ticker}</strong></td><td>{setupLabel(t)}</td><td>{t.plan_id ? <button className="terminal-link" onClick={()=>navigate('/trade-plans?ticker=' + encodeURIComponent(t.ticker) + '&plan=' + encodeURIComponent(t.plan_id))}>{t.plan_id.slice(0,12)}…</button> : '—'}</td><td className={`number ${Number(t.pnl)>=0?'positive':'negative'}`}>{money(t.pnl)}</td><td className="number">{fixed(t.r_multiple)}</td><td className="number">{fixed(t.mfe_r)}R</td><td className="number">{fixed(t.mae_r)}R</td><td>{regimeLabel(t)}</td><td>{themeLabel(t)}</td><td className="number">{fixed(t.entry_price)}</td><td className="number">{fixed(t.exit_price)}</td><td>{t.closed_at ? new Date(t.closed_at).toLocaleString() : '—'}</td></tr>)}
        </tbody></table></div> : <div className="empty-state"><strong>No closed trades yet</strong>Completed execution history will appear here automatically.</div>}
      </section>
    </div>
  </div>;
};

export default Analytics;
